import { useTranslation } from 'react-i18next';
import { ImagePlus } from 'lucide-react';
import { Button } from '../../ui/button';
import { CircuitOverlayButtons, type CircuitModeControls } from '../CircuitOverlayButtons';
import { OVERLAY_ATTRIBUTE } from '../board-grid/useZoomPan';

/**
 * Making the climb a circuit, and reading the circuit back — the same pair of
 * buttons every wall has, here keeping the change-photo button company.
 */
export type ClimbPhotoCircuitControls = CircuitModeControls;

interface ClimbPhotoOverlayActionsProps {
  onChange: () => void;
  /**
   * Circuit mode, where the climb can have one: a boulder is circled hold by
   * hold, so its circles can carry an order. A route is one line from the ground
   * to the anchor and has nothing to put in sequence.
   */
  circuit?: ClimbPhotoCircuitControls;
}

/**
 * Changing the picture — and, beside it, making the climb a circuit — top-left
 * of the photo.
 *
 * In overlay because the photo runs edge to edge and there is no chrome around
 * it to hang a control on. The corners are spoken for: the zoom's way out is
 * bottom-left, and what acts on the whole drawing is top-right.
 *
 * There is no way to *remove* the photo. Every circle and every point is stored
 * as a fraction of it, so taking it away would take the drawing with it — and a
 * setter who wants a different picture wants exactly that, which is what
 * changing it does.
 */
export function ClimbPhotoOverlayActions({ onChange, circuit }: ClimbPhotoOverlayActionsProps) {
  const { t } = useTranslation();

  return (
    <div
      className="absolute left-2 top-2 flex items-center gap-1"
      // Sat on the picture, not in it: the gesture that moved the photo has no
      // claim on this click, and the surface underneath hit-tests presses.
      {...{ [OVERLAY_ATTRIBUTE]: '' }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <Button
        type="button"
        size="icon"
        variant="secondary"
        onClick={onChange}
        aria-label={t('climbEditor.photo.change')}
        title={t('climbEditor.photo.change')}
        className="size-9 rounded-full bg-black/60 text-white shadow-lg backdrop-blur hover:bg-black/75"
      >
        <ImagePlus className="size-4" aria-hidden />
      </Button>

      {circuit && <CircuitOverlayButtons circuit={circuit} />}
    </div>
  );
}
